import { Activity, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Division, EmptyState, Flex, Heading, Input, Table, Popover, Button, Icon, Icons, Paragraph, useToast } from "@ir/ui";
import { PromoDetail } from "@/core/entities/promotion/PromoDetail";
import { usePromotionDrawer, usePromotionProduct, usePromoProductExport } from "../hooks";
import { createProductsInScopeTableColumns } from "../utils/promotionProductsTableColumns";
import { getPromotionSearchKeywords } from "../utils/promotionDisplay.utils";
import { getBlankPromoProducts } from "@/core/entities/promotion";
import { PromotionsPagination } from ".";
import ProductsInScopeChip from "./ProductsInScopeChip";

const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_LENGTH = 100;
interface ProductsInScopeTabProps {
  promoDetail: PromoDetail | null;
}

const ProductsInScopeTab = memo(({ promoDetail }: ProductsInScopeTabProps) => {
  const [isInvalidSearch, setIsInvalidSearch] = useState(false);
  const productsTableContainerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [searchTextTempValue, setSearchTextTempValue] = useState("");
  const [isOpenSearchInfoPopover, setIsOpenSearchInfoPopover] = useState(false);

  const {
    isLoading,
    isRefreshing,
    promoProducts,
    filterInfo,
    columns: promoProductsColumns,
    currentPage,
    pageLimit,
    totalCount,
    isAllProductsInScope,
    getPromoProductsByPromoEventId,
    setFilter,
    setCurrentPage,
  } = usePromotionProduct();

  const { isPromoDetailLoading } = usePromotionDrawer();
  const { handlePromoProductExport, isPromoProductExporting, setIsPromoProductExporting } = usePromoProductExport();
  const { error: errorToast } = useToast();

  const searchKeywords = useMemo(() => getPromotionSearchKeywords(filterInfo.search), [filterInfo.search]);
  const isRefreshingOrLoading = useMemo(
    () => isRefreshing || isLoading || isPromoDetailLoading,
    [isRefreshing, isLoading, isPromoDetailLoading]
  );

  const hasActiveSearch = Boolean(filterInfo.search?.trim());

  const showEmptyState = useMemo(
    () => !isRefreshingOrLoading && totalCount === 0,
    [isRefreshingOrLoading, totalCount]
  );

  const validateSearch = (value: string): boolean => {
    const searchValue = value.trim();
    if (searchValue.length === 0) {
      return false;
    } else if (searchValue.length < MIN_SEARCH_LENGTH) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (promoDetail?.promoEventId) {
      getPromoProductsByPromoEventId({ promoEventId: promoDetail.promoEventId, isLoading: false, isRefreshing: true });
    }
  }, [promoDetail?.promoEventId, getPromoProductsByPromoEventId]);

  // Sync local input with atom filter (e.g. on external reset)
  useEffect(() => {
    setTimeout(() => {
      setSearchTextTempValue(filterInfo?.search ?? "");
    }, 0);
  }, [filterInfo?.search]);

  // Close popover on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpenSearchInfoPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, []);

  const handleSearch = useCallback(
    async (value: string) => {
      if (!promoDetail?.promoEventId) return;
      const searchValue = value.trim();

      const isSearchInvalid = validateSearch(searchValue);
      setIsInvalidSearch(isSearchInvalid);
      if (isSearchInvalid) {
        setIsOpenSearchInfoPopover(true);
        return;
      }

      setSearchTextTempValue(searchValue);
      setFilter({
        ...filterInfo,
        search: searchValue.length > 0 ? searchValue : null,
      });
      setCurrentPage(1);
      await getPromoProductsByPromoEventId({
        promoEventId: promoDetail.promoEventId,
        isLoading: false,
        isRefreshing: true,
      });
    },
    [filterInfo, setFilter, setCurrentPage, getPromoProductsByPromoEventId, promoDetail]
  );

  const handleClear = useCallback(async () => {
    if (!promoDetail?.promoEventId) return;
    setSearchTextTempValue("");
    setFilter({
      ...filterInfo,
      search: null,
    });
    setCurrentPage(1);
    await getPromoProductsByPromoEventId({
      promoEventId: promoDetail.promoEventId,
      isLoading: false,
      isRefreshing: true,
    });
  }, [filterInfo, setFilter, setCurrentPage, getPromoProductsByPromoEventId, promoDetail]);

  const columns = useMemo(() => {
    return createProductsInScopeTableColumns({
      columns: promoProductsColumns,
      searchKeywords,
      isRefreshing: isRefreshingOrLoading,
    });
  }, [promoProductsColumns, searchKeywords, isRefreshingOrLoading]);

  const blankPromoProducts = useMemo(() => getBlankPromoProducts(10), []);

  const dataSource = useMemo(() => {
    if (showEmptyState) {
      return [];
    }
    if (isRefreshingOrLoading || (promoProducts.length === 0 && !isLoading)) {
      return blankPromoProducts;
    }
    return promoProducts;
  }, [showEmptyState, isRefreshingOrLoading, blankPromoProducts, promoProducts, isLoading]);

  const handlePageChange = (page: number) => {
    if (!promoDetail?.promoEventId) return;
    setCurrentPage(page);
    getPromoProductsByPromoEventId({
      promoEventId: promoDetail.promoEventId,
      isLoading: false,
      isRefreshing: true,
    });
  };

  const handleDownloadSKU = useCallback(async () => {
    if (!promoDetail?.promoEventId) return;

    try {
      const response = await handlePromoProductExport(promoDetail.promoEventId, filterInfo.search);
      if (response?.success) {
        globalThis.open(response.signedUrl, "_blank");
      } else {
        errorToast({
          content: "Failed to export products in scope",
        });
      }
    } catch {
      errorToast({
        content: "Failed to export products in scope",
      });
    } finally {
      setIsPromoProductExporting(false);
    }
  }, [promoDetail?.promoEventId, filterInfo.search, handlePromoProductExport, errorToast, setIsPromoProductExporting]);

  const isExportDisabled = isRefreshingOrLoading || totalCount === 0;

  return (
    <>
      <Flex className="w-100 pb-3" gap={1} justifyContent="space-between">
        <Flex className="w-50">
          <Input
            size="small"
            type="text"
            placeholder="Search"
            aria-label="Search Promotion Products in Scope"
            id="promotion-products-search"
            className="rounded-medium"
            allowClear={true}
            isWarning={isInvalidSearch}
            minLength={MIN_SEARCH_LENGTH}
            maxLength={MAX_SEARCH_LENGTH}
            showCharCount={true}
            value={searchTextTempValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTextTempValue(e.target.value)}
            onClear={handleClear}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                handleSearch(e.currentTarget.value);
              }
            }}
            prefix={
              <Popover
                focusable={true}
                isOpen={isOpenSearchInfoPopover}
                placement="bottom left"
                isNonModal={true}
                shouldCloseOnInteractOutside={() => true}
                defaultOpen={true}
                content={
                  <Division className="text-xs text-grey-700" ref={popoverRef}>
                    <ul style={{ listStyleType: "circle", paddingLeft: "16px" }}>
                      <li className="mb-1">
                        Search by <b>Product Name</b> or <b>UPC/Item No.</b>
                      </li>
                      <li className="mb-1">
                        Minimum search length is <b>{MIN_SEARCH_LENGTH} characters</b>
                      </li>
                      <li className="mb-1">
                        Maximum search length is <b>{MAX_SEARCH_LENGTH} characters</b>
                      </li>
                    </ul>
                  </Division>
                }
              >
                <Button
                  variant="outline"
                  size="small"
                  noStyle
                  onPress={() => setIsOpenSearchInfoPopover(!isOpenSearchInfoPopover)}
                  aria-label="Open search info"
                >
                  <Icon name={Icons.InfoCircle} size="small" color="grey" style={{ color: "var(--grey-400)" }} />
                </Button>
              </Popover>
            }
          />
        </Flex>
        <Activity mode={promoProducts.length > 0 ? "visible" : "hidden"}>
          <Button
            variant="clear"
            size="small"
            color="grey"
            htmlTitle="Download SKU"
            isDisabled={isExportDisabled || isPromoProductExporting}
            onPress={handleDownloadSKU}
          >
            <Icon
              name={isPromoProductExporting ? Icons.Loading : Icons.Download01}
              size="small"
              slot="start"
              className={isPromoProductExporting ? "icon-fast-loading" : ""}
            />
            Download SKU
          </Button>
        </Activity>
      </Flex>
      <Flex className="pb-3">
        {isAllProductsInScope && <ProductsInScopeChip
          isLoading={isRefreshingOrLoading}
        />}
      </Flex>
      <Flex direction="column" className="flex-1" style={{ height: "calc(100% - 120px)" }} align="center">
        {showEmptyState ? (
          <EmptyState
            className="bg-white px-4 py-5 w-100"
            icon={Icons.File}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                No products found
              </Heading>
            }
            description={
              <Paragraph className="text-grey-500 font-regular mb-0">
                {hasActiveSearch
                  ? "No products match your search. Try a different Product Name or UPC/Item No."
                  : "No products are in scope for this promotion."}
              </Paragraph>
            }
          />
        ) : (
          <Table
            ref={productsTableContainerRef}
            sticky
            border="row"
            aria-label="Products In Scope Table"
            scroll={{ x: "100%", y: "100%" }}
            columns={columns}
            dataSource={dataSource}
          />
        )}
      </Flex>
      <PromotionsPagination
        hasPagination={!showEmptyState && totalCount > pageLimit}
        totalCount={totalCount}
        pageLimit={pageLimit}
        currentPage={currentPage}
        isLoading={isLoading || isRefreshing}
        onPageChange={handlePageChange}
      />
    </>
  );
});

ProductsInScopeTab.displayName = "ProductsInScopeTab";

export default ProductsInScopeTab;
